import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconButton } from '@mui/material';

import { useCallback } from 'react';
import useMerchant from 'src/hooks/useMerchant';
import Page from 'src/components/Page';
import { getProgramMenu } from './dashboard/navbar/NavConfig';
import { AppTabs } from 'src/components/app-tabs';
import ProgrammeRenameDialog from './dashboard/dialogs/ProgrammeRenameDialog';
import { useMutationProgrammeUpdate } from 'src/resources/queries/programmes.queries';
import { useToggle } from 'src/hooks/useToggle';
import { StackColumn } from 'src/components';
import { firstLetterUpperCase } from 'src/utils/stringFormatter';
import { IconPencil } from 'src/libraries/icons';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useProgramme } from 'src/hooks/use-programme';

//
//

const ProgramsLayout: React.FC = () => {
  const { id, type: programmeType, code: programmeCode } = useParams();
  const { merchant, isMerchantLoading } = useMerchant();
  const { programmes, programme } = useProgramme(programmeCode);
  const toggle = useToggle();
  const navigate = useNavigate();
  const location = useLocation();

  const updateProgramme = useMutationProgrammeUpdate();

  const menuItems = programmes && getProgramMenu(programmes);
  const currentOpenedProgram = menuItems?.find((el) => el?.code === programmeCode);

  const onUpdateMerchantProgrammeName = useCallback(async (newName: string) => {
    if (!merchant || !currentOpenedProgram || !programme) {
      return;
    }

    try {
      const response = await updateProgramme.mutateAsync({
        merchantCode: merchant.code,
        programmeCode: currentOpenedProgram.code,
        name: newName,
        // Note: passing only `name` erases preferred basket code in programme
        preferredBasketCode: programme?.preferredBasketCode,
      });

      if (response?.status === 204) {
        toggle.off();
      }
    } catch (e) {
      console.error('Update programme name', e);
    }
  }, [currentOpenedProgram, merchant, programme, toggle, updateProgramme]);

  if (location.pathname.includes('offset') && id) return <Outlet />;

  const selectedTab = currentOpenedProgram?.programmeMenu?.find((tab) =>
    location.pathname.includes(tab.path)
  )?.path;

  const handleChange = (_: React.SyntheticEvent, value: string) => {
    if (!programmeType || !programmeCode) return;

    navigate(value, { viewTransition: true });
  };

  if (!programmeType || !programmeCode) {
    return <Page title="Not found">Programme not found</Page>;
  }

  //

  return (
    <Page
      title={currentOpenedProgram?.title || 'Loading...'}
      titleActions={
        (currentOpenedProgram?.code &&
          !['EVENT', 'CUSTOMER', 'BUSINESS'].includes(currentOpenedProgram.code) && (
            <IconButton
              disabled={isMerchantLoading || updateProgramme?.isPending}
              onClick={toggle.on}
              title="Update programme name"
            >
              <IconPencil size={18} />
            </IconButton>
          )) ||
        undefined
      }
      breadcrumbs={[
        { name: 'Programmes', href: PATH_DASHBOARD.programs.root },
        {
          name: firstLetterUpperCase(selectedTab?.split('/').filter(Boolean).pop()),
          href: selectedTab,
        },
      ]}
    >
      <StackColumn gap={3} flex={1}>
        <AppTabs
          value={selectedTab}
          onChange={handleChange}
          tabs={currentOpenedProgram?.programmeMenu?.map((v) => ({
            ...v,
            value: v?.path,
            label: v?.title,
          }))}
        />

        <Outlet />
      </StackColumn>

      <ProgrammeRenameDialog
        toggle={toggle}
        isLoading={updateProgramme.isPending}
        currentName={currentOpenedProgram?.title || ''}
        onRename={onUpdateMerchantProgrammeName}
      />
    </Page>
  );
};

export default ProgramsLayout;
